import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    FormControl,
    FormControlLabel,
    InputLabel,
    Link,
    MenuItem,
    Paper,
    Select,
    Snackbar,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import YouTubeIcon from "@mui/icons-material/YouTube";
import { config } from "../Const";
import { executeFetchAsync } from "../Util";

const TEMPLATES = [
    ["WRONG_ANSWER", "Wrong answer"],
    ["FULL_PLAYBACK", "Full playback"],
    ["BEAT_THE_CROWD", "Beat the crowd"],
    ["BEAT_THE_CLOCK", "Beat the clock"],
    ["THREE_FACTS", "Three facts"],
];

const CATEGORIES = [
    ["10", "Music"],
    ["22", "People & Blogs"],
    ["24", "Entertainment"],
    ["27", "Education"],
];

const cleanTags = (value) => value.split(",").map((tag) => tag.trim()).filter(Boolean);

export default function YouTubeShort({ seoTitle, type, detailedEntity }) {
    const [template, setTemplate] = useState("WRONG_ANSWER");
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [tags, setTags] = useState("");
    const [categoryId, setCategoryId] = useState("10");
    const [privacyStatus, setPrivacyStatus] = useState("public");
    const [madeForKids, setMadeForKids] = useState(false);
    const [containsSyntheticMedia, setContainsSyntheticMedia] = useState(false);
    const [notifySubscribers, setNotifySubscribers] = useState(true);
    const [scheduleEnabled, setScheduleEnabled] = useState(false);
    const [publishAt, setPublishAt] = useState("");
    const [connection, setConnection] = useState(null);
    const [connectionLoading, setConnectionLoading] = useState(true);
    const [loginPending, setLoginPending] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [publication, setPublication] = useState(null);
    const [message, setMessage] = useState(null);
    const popupTimer = useRef(null);

    useEffect(() => {
        setTitle("");
        setDescription("");
        setTags("");
        setPublication(null);
    }, [seoTitle, template]);

    useEffect(() => () => {
        if (popupTimer.current) window.clearInterval(popupTimer.current);
    }, []);

    useEffect(() => () => {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
    }, [previewUrl]);

    const loadConnection = useCallback(async () => {
        setConnectionLoading(true);
        try {
            const result = await executeFetchAsync(`${config.SERVER_URL}/api/v1/admin/youtube/connection`, "get");
            setConnection(result);
        } catch (error) {
            setConnection(null);
            setMessage({ severity: "error", text: `Could not read YouTube connection: ${error.message}` });
        } finally {
            setConnectionLoading(false);
        }
    }, []);

    useEffect(() => { void loadConnection(); }, [loadConnection]);

    useEffect(() => {
        const receiveOAuth = (event) => {
            if (event.data?.type !== "quizmyday-youtube-oauth") return;
            setLoginPending(false);
            if (popupTimer.current) window.clearInterval(popupTimer.current);
            if (event.data.success) {
                setMessage({ severity: "success", text: "YouTube connected" });
                void loadConnection();
            } else {
                setMessage({ severity: "error", text: "YouTube authorization failed" });
            }
        };
        window.addEventListener("message", receiveOAuth);
        return () => window.removeEventListener("message", receiveOAuth);
    }, [loadConnection]);

    const connectYouTube = async () => {
        const popup = window.open("", "youtubeLogin", "width=620,height=760,resizable=yes,scrollbars=yes");
        if (!popup) {
            setMessage({ severity: "warning", text: "Allow popups to connect YouTube" });
            return;
        }
        setLoginPending(true);
        try {
            const response = await executeFetchAsync(
                `${config.SERVER_URL}/api/v1/admin/youtube/authorization-url`, "get");
            popup.location.replace(response.authorizationUrl);
            popupTimer.current = window.setInterval(() => {
                if (popup.closed) {
                    window.clearInterval(popupTimer.current);
                    setLoginPending(false);
                    void loadConnection();
                }
            }, 600);
        } catch (error) {
            popup.close();
            setLoginPending(false);
            setMessage({ severity: "error", text: `Could not start YouTube login: ${error.message}` });
        }
    };

    const validationError = useMemo(() => {
        if (type !== "QUIZ") return "YouTube Shorts can only be generated for quiz pages.";
        if (!title.trim()) return "Title is required.";
        if (title.length > 100) return "Title cannot exceed 100 characters.";
        if (!description.trim()) return "Description is required.";
        if (new TextEncoder().encode(description).length > 5000) return "Description cannot exceed 5000 UTF-8 bytes.";
        if (cleanTags(tags).join(",").length > 500) return "Tags cannot exceed 500 characters in total.";
        if (scheduleEnabled && privacyStatus !== "private") return "Scheduled videos must use Private visibility.";
        if (scheduleEnabled && !publishAt) return "Choose a scheduled publication time.";
        if (scheduleEnabled && new Date(publishAt).getTime() <= Date.now()) return "Scheduled publication must be in the future.";
        return null;
    }, [description, privacyStatus, publishAt, scheduleEnabled, tags, title, type]);

    const requestBody = () => ({
        quizSeoTitle: seoTitle,
        template,
        socialMedia: "YOUTUBE",
        title: title.trim(),
        description: description.trim(),
        tags: cleanTags(tags),
        categoryId,
        privacyStatus,
        madeForKids,
        containsSyntheticMedia,
        notifySubscribers,
        publishAt: scheduleEnabled && publishAt ? new Date(publishAt).toISOString() : null,
    });

    const generatePreview = async () => {
        setGenerating(true);
        try {
            const blob = await executeFetchAsync(
                `${config.SERVER_URL}/api/v1/admin/teaser-video/generate`,
                "post", { "Content-Type": "application/json" },
                JSON.stringify({ quizSeoTitle: seoTitle, template, socialMedia: "YOUTUBE" }));
            if (previewUrl) URL.revokeObjectURL(previewUrl);
            setPreviewUrl(URL.createObjectURL(blob));
            const query = new URLSearchParams({
                quizSeoTitle: seoTitle,
                template,
                socialMedia: "YOUTUBE",
            });
            const generatedPublication = await executeFetchAsync(
                `${config.SERVER_URL}/api/v1/admin/teaser-video/publication?${query}`, "get");
            setTitle(generatedPublication.title || "");
            setDescription(generatedPublication.description || "");
            setTags((generatedPublication.youtubeTags || []).join(", "));
            setCategoryId(generatedPublication.youtubeCategoryId || "10");
            setPrivacyStatus(generatedPublication.youtubePrivacyStatus || "public");
            setMadeForKids(Boolean(generatedPublication.youtubeMadeForKids));
            setContainsSyntheticMedia(Boolean(
                generatedPublication.youtubeContainsSyntheticMedia));
            setNotifySubscribers(
                generatedPublication.youtubeNotifySubscribers !== false);
            setPublication(generatedPublication);
            setMessage({
                severity: "success",
                text: "Preview and YouTube metadata generated by AI",
            });
        } catch (error) {
            setMessage({ severity: "error", text: `Preview generation failed: ${error.message}` });
        } finally {
            setGenerating(false);
        }
    };

    const publishVideo = async () => {
        if (validationError) {
            setMessage({ severity: "warning", text: validationError });
            return;
        }
        setPublishing(true);
        try {
            const result = await executeFetchAsync(
                `${config.SERVER_URL}/api/v1/admin/teaser-video/publish`,
                "post", { "Content-Type": "application/json" }, JSON.stringify(requestBody()));
            setPublication(result);
            setMessage({ severity: "success", text: "YouTube Short uploaded successfully" });
        } catch (error) {
            setMessage({ severity: "error", text: `YouTube upload failed: ${error.message}` });
        } finally {
            setPublishing(false);
        }
    };

    if (type !== "QUIZ") {
        return <Alert severity="info">Select a quiz to generate and upload a YouTube Short.</Alert>;
    }

    return (
        <Stack spacing={3} sx={{ width: "100%" }}>
            <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }} justifyContent="space-between">
                    <Box>
                        <Typography variant="subtitle1" fontWeight={700}>YouTube channel</Typography>
                        {connectionLoading ? <CircularProgress size={20} /> : connection?.connected ? (
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                <Chip color="success" size="small" label="Connected" />
                                <Typography variant="body2">{connection.channelTitle} ({connection.channelHandle})</Typography>
                                <Typography variant="caption" color="text.secondary">{connection.channelId}</Typography>
                            </Stack>
                        ) : <Typography variant="body2" color="error">YouTube is not connected.</Typography>}
                    </Box>
                    <Button variant={connection?.connected ? "outlined" : "contained"} color="error"
                            startIcon={loginPending ? <CircularProgress size={16} /> : <YouTubeIcon />}
                            disabled={loginPending} onClick={connectYouTube}>
                        {connection?.connected ? "Reconnect YouTube" : "Connect YouTube"}
                    </Button>
                </Stack>
            </Paper>

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.3fr) minmax(300px, .7fr)" }, gap: 3 }}>
                <Stack spacing={2}>
                    <FormControl fullWidth>
                        <InputLabel id="youtube-template-label">Teaser format</InputLabel>
                        <Select labelId="youtube-template-label" value={template} label="Teaser format"
                                onChange={(event) => setTemplate(event.target.value)}>
                            {TEMPLATES.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <TextField required fullWidth label="Title" value={title}
                               onChange={(event) => setTitle(event.target.value)}
                               helperText={`${title.length}/100 characters`} inputProps={{ maxLength: 100 }} />
                    <TextField required fullWidth multiline minRows={7} label="Description" value={description}
                               onChange={(event) => setDescription(event.target.value)}
                               helperText={`${new TextEncoder().encode(description).length}/5000 bytes`} />
                    <TextField fullWidth label="Tags" value={tags} onChange={(event) => setTags(event.target.value)}
                               helperText="Comma-separated. Tags do not replace hashtags in the description." />
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                        <FormControl fullWidth>
                            <InputLabel id="youtube-category-label">Category</InputLabel>
                            <Select labelId="youtube-category-label" value={categoryId} label="Category"
                                    onChange={(event) => setCategoryId(event.target.value)}>
                                {CATEGORIES.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth>
                            <InputLabel id="youtube-privacy-label">Visibility</InputLabel>
                            <Select labelId="youtube-privacy-label" value={privacyStatus} label="Visibility"
                                    onChange={(event) => {
                                        setPrivacyStatus(event.target.value);
                                        if (event.target.value !== "private") setScheduleEnabled(false);
                                    }}>
                                <MenuItem value="public">Public</MenuItem>
                                <MenuItem value="unlisted">Unlisted</MenuItem>
                                <MenuItem value="private">Private</MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>
                    <FormControlLabel control={<Checkbox checked={madeForKids}
                        onChange={(event) => setMadeForKids(event.target.checked)} />}
                        label="Made for kids" />
                    <FormControlLabel control={<Checkbox checked={containsSyntheticMedia}
                        onChange={(event) => setContainsSyntheticMedia(event.target.checked)} />}
                        label="Contains realistic altered or synthetic content" />
                    <FormControlLabel control={<Checkbox checked={notifySubscribers}
                        onChange={(event) => setNotifySubscribers(event.target.checked)} />}
                        label="Notify subscribers" />
                    <FormControlLabel disabled={privacyStatus !== "private"}
                        control={<Checkbox checked={scheduleEnabled}
                            onChange={(event) => setScheduleEnabled(event.target.checked)} />}
                        label="Schedule publication" />
                    {scheduleEnabled && (
                        <TextField type="datetime-local" required label="Publish at" value={publishAt}
                                   onChange={(event) => setPublishAt(event.target.value)}
                                   InputLabelProps={{ shrink: true }} />
                    )}
                    {validationError && <Alert severity="warning">{validationError}</Alert>}
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                        <Button variant="outlined" disabled={generating || publishing} onClick={generatePreview}
                                startIcon={generating ? <CircularProgress size={16} /> : null}>
                            Generate preview
                        </Button>
                        <Button variant="contained" color="error" startIcon={publishing ? <CircularProgress size={16} /> : <YouTubeIcon />}
                                disabled={publishing || generating || !connection?.connected || Boolean(validationError)}
                                onClick={publishVideo}>
                            Upload YouTube Short
                        </Button>
                    </Stack>
                </Stack>

                <Paper variant="outlined" sx={{ p: 2, minHeight: 320 }}>
                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>Video preview</Typography>
                    {previewUrl ? (
                        <Box component="video" src={previewUrl} controls sx={{ width: "100%", maxHeight: 620, bgcolor: "black", borderRadius: 1 }} />
                    ) : (
                        <Box sx={{ minHeight: 260, display: "grid", placeItems: "center", bgcolor: "grey.100", borderRadius: 1, p: 3 }}>
                            <Typography color="text.secondary" textAlign="center">Generate a preview to inspect the exact vertical video before uploading.</Typography>
                        </Box>
                    )}
                    {publication && (
                        <Alert severity={publication.status === "PUBLISHED" ? "success" : "info"} sx={{ mt: 2 }}>
                            <Typography variant="body2">Status: {publication.status}</Typography>
                            {publication.externalPublicationUrl && (
                                <Link href={publication.externalPublicationUrl} target="_blank" rel="noreferrer">Open the published Short</Link>
                            )}
                        </Alert>
                    )}
                </Paper>
            </Box>

            <Snackbar open={Boolean(message)} autoHideDuration={6000} onClose={() => setMessage(null)}>
                {message ? <Alert severity={message.severity} onClose={() => setMessage(null)}>{message.text}</Alert> : undefined}
            </Snackbar>
        </Stack>
    );
}
